import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  Users,
  ShoppingBag,
  Activity,
  Server,
  RefreshCw,
  Loader2,
  UserPlus,
  UserMinus,
  UserCheck,
  CalendarDays,
  LogIn,
  KeyRound,
  TrendingUp,
  TrendingDown,
  Minus,
  ShoppingCart,
  Banknote,
  CreditCard,
  BadgeCheck,
  Repeat,
  Wallet,
  Tag,
  MessageCircle,
  Reply,
  ShieldCheck,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  ComposedChart,
} from 'recharts';
import { useProxyHealthQuery } from '@/client/api/dashboard/health.query';
import { useProxyMetricsQuery } from '@/client/api/dashboard/metrics.query';
import { useAdminAnalyticsQuery } from '@/client/api/analytics/analytics.query';
import {
  TrendPeriod,
  TrendMode,
  DateRange,
  defaultRangeForPeriod,
  toInputDate,
  fromInputDate,
  formatRangeLabel,
  RANGE_PRESETS,
  rangesEqual,
  toCumulativeSeries,
} from '@/client/utils/dashboard-trends';
import {
  overviewToSeries,
  overviewToCommerce,
  overviewToKpis,
  overviewToMix,
  overviewToTypeSeries,
} from '@/client/utils/dashboard-overview';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { PERMISSIONS } from '@/client/config/permissions';

const numberFmt = new Intl.NumberFormat('en-US');
const moneyFmt = (amount: number, ccode: string) => `${ccode} ${numberFmt.format(Math.round(amount))}`;
const pctFmt = new Intl.NumberFormat('en-US', {
  style: 'percent',
  maximumFractionDigits: 1,
});

type CompactKpiProps = {
  label: string;
  value: string;
  hint?: string;
  delta?: { pct: number; direction: 'up' | 'down' | 'flat' };
  deltaInvert?: boolean;
  icon: React.ComponentType<{ className?: string }>;
  to?: string;
};

const CompactKpi: React.FC<CompactKpiProps> = ({
  label,
  value,
  hint,
  delta,
  deltaInvert,
  icon: Icon,
  to,
}) => {
  const upIsGood = !deltaInvert;
  const DeltaIcon =
    delta?.direction === 'up' ? TrendingUp : delta?.direction === 'down' ? TrendingDown : Minus;
  const deltaColor =
    !delta || delta.direction === 'flat'
      ? 'text-gray-400'
      : (delta.direction === 'up') === upIsGood
        ? 'text-emerald-600'
        : 'text-rose-600';

  const frame = cn(
    'flex min-w-0 items-center gap-2.5 rounded-md border border-gray-200 bg-white px-3 py-2 text-left',
    to && 'cursor-pointer no-underline hover:border-gray-300 hover:bg-gray-50',
  );
  const body = (
    <>
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-50 text-gray-600">
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[11px] font-medium uppercase tracking-wide text-gray-400">
          {label}
        </p>
        <div className="flex items-baseline gap-1.5">
          <p className="truncate text-base font-semibold tabular-nums text-gray-900">{value}</p>
          {delta && !(delta.direction === 'flat' && delta.pct === 0) && (
            <span className={cn('inline-flex items-center gap-0.5 text-[10px] font-medium', deltaColor)}>
              <DeltaIcon className="h-2.5 w-2.5" />
              {delta.pct > 0 ? '+' : ''}
              {delta.pct}%
            </span>
          )}
        </div>
        {hint && <p className="truncate text-[10px] text-gray-400">{hint}</p>}
      </div>
    </>
  );
  return to ? (
    <Link to={to} className={frame}>{body}</Link>
  ) : (
    <div className={frame}>{body}</div>
  );
};

const PERIODS: { value: TrendPeriod; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

type ChartSeries = {
  /** A field on the row, or a figure worked out from it (a rate). */
  key: string | ((row: any) => number);
  name: string;
  color: string;
  kind: 'bar' | 'line';
  stack?: string;
  /** Drawn against the right-hand axis. */
  right?: boolean;
  fmt?: (v: number) => string;
};

const pctOf = (part: number, whole: number) => (whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0);
const pctLabel = (v: number) => `${v}%`;

const TYPE_COLORS = ['#4F46E5', '#059669', '#D97706', '#0891B2', '#E11D48', '#7C3AED', '#94A3B8'];

/** Bars and lines over the shared period buckets, with an optional right axis. */
const TrendChart: React.FC<{
  title: string;
  subtitle: string;
  data: object[];
  series: ChartSeries[];
  rightFmt?: (v: number) => string;
  action?: React.ReactNode;
}> = ({ title, subtitle, data, series, rightFmt, action }) => {
  const fmtByName = new Map(series.map((s) => [s.name, s.fmt ?? numberFmt.format]));
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
          <p className="text-[10px] text-gray-400">{subtitle}</p>
        </div>
        {action}
      </div>
      <div className="h-52">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} />
            <YAxis
              yAxisId="left"
              allowDecimals={false}
              tick={{ fontSize: 10, fill: '#9ca3af' }}
              width={40}
            />
            {series.some((s) => s.right) && (
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                tickFormatter={rightFmt}
                width={44}
              />
            )}
            <Tooltip
              contentStyle={{ fontSize: 12 }}
              formatter={(v: number, name: string) => (fmtByName.get(name) ?? numberFmt.format)(v)}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {series.map((s) =>
              s.kind === 'bar' ? (
                <Bar
                  key={s.name}
                  yAxisId={s.right ? 'right' : 'left'}
                  dataKey={s.key}
                  name={s.name}
                  fill={s.color}
                  stackId={s.stack}
                  maxBarSize={28}
                />
              ) : (
                <Line
                  key={s.name}
                  yAxisId={s.right ? 'right' : 'left'}
                  type="monotone"
                  dataKey={s.key}
                  name={s.name}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 3 }}
                />
              ),
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const MixCard: React.FC<{
  title: string;
  subtitle: string;
  rows?: { name: string; count: number }[];
  color: string;
  loading: boolean;
}> = ({ title, subtitle, rows = [], color, loading }) => {
  const total = rows.reduce((sum, row) => sum + row.count, 0);
  const max = Math.max(1, ...rows.map((row) => row.count));
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
      <h2 className="mb-2 text-sm font-semibold text-gray-900">{title}</h2>
      <p className="mb-2 text-[10px] text-gray-400">{subtitle}</p>
      {rows.length === 0 ? (
        <p className="py-6 text-center text-[11px] text-gray-400">
          {loading ? 'Loading…' : 'Nothing recorded yet'}
        </p>
      ) : (
        <ul className="max-h-48 space-y-1.5 overflow-y-auto">
          {rows.map((row) => (
            <li key={row.name} className="flex items-center gap-2">
              <span className="w-28 shrink-0 truncate text-[11px] capitalize text-gray-600" title={row.name}>
                {row.name}
              </span>
              <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-100">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${Math.max(3, (row.count / max) * 100)}%`, backgroundColor: color }}
                />
              </div>
              <span className="w-10 shrink-0 text-right text-[11px] tabular-nums text-gray-800">
                {numberFmt.format(row.count)}
              </span>
              <span className="w-10 shrink-0 text-right text-[10px] tabular-nums text-gray-400">
                {pctLabel(pctOf(row.count, total))}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const TREND_MODES: { value: TrendMode; label: string }[] = [
  { value: 'period', label: 'Per period' },
  { value: 'cumulative', label: 'Cumulative' },
];

const Dashboard = () => {
  const [period, setPeriod] = useState<TrendPeriod>('weekly');
  const [trendMode, setTrendMode] = useState<TrendMode>('period');
  const [staffLogins, setStaffLogins] = useState(true);
  const [range, setRange] = useState<DateRange>(() => defaultRangeForPeriod('weekly'));
  const healthQuery = useProxyHealthQuery();
  const metricsQuery = useProxyMetricsQuery();

  const analyticsQuery = useAdminAnalyticsQuery(range);
  const overview = analyticsQuery.data;

  const trendSeries = useMemo(
    () => overviewToSeries(overview, period, range),
    [overview, period, range],
  );
  const chartSeries = useMemo(
    () => (trendMode === 'cumulative' ? toCumulativeSeries(trendSeries) : trendSeries),
    [trendMode, trendSeries],
  );
  const kpis = useMemo(() => overviewToKpis(overview, trendSeries), [overview, trendSeries]);
  const commerce = useMemo(() => overviewToCommerce(overview), [overview]);
  const { listingMix, engagementMix: engagementTypes } = useMemo(
    () => overviewToMix(overview),
    [overview],
  );
  const typeSeries = useMemo(
    () => overviewToTypeSeries(overview, period, range, trendMode === 'cumulative'),
    [overview, period, range, trendMode],
  );
  const maxEngagement = engagementTypes[0]?.count || 1;
  const maxListingCat = listingMix[0]?.count || 1;

  const activePresetId = RANGE_PRESETS.find((p) => rangesEqual(p.getRange(), range))?.id ?? null;
  const modeLabel = trendMode === 'cumulative' ? 'cumulative' : 'per period';
  const cumulative = trendMode === 'cumulative';
  // A week or month holds several days of distinct counts; the bucket is their average.
  const perDay = period === 'daily' ? '' : 'Avg ';

  const chartSubtitle = `${period} · ${modeLabel} · ${formatRangeLabel(range)}`;
  const funnel = [
    { label: 'Visitors', count: commerce.rangeVisitors, unit: 'people' },
    { label: 'Signups', count: kpis.signups, unit: 'people' },
    { label: 'Listings posted', count: kpis.newListings, unit: 'listings' },
    { label: 'Listings inquired', count: commerce.inquiredInRange, unit: 'listings' },
    { label: 'Listings sold', count: commerce.soldListings, unit: 'listings' },
  ];
  const maxFunnel = Math.max(1, ...funnel.map((f) => f.count));

  // The header scrolls away with the page (the layout's overflow rules out
  // `sticky`), so the two switches reappear in a floating bar once it is gone.
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerVisible, setHeaderVisible] = useState(true);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) => setHeaderVisible(entry.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const handlePeriodChange = (next: TrendPeriod) => {
    setPeriod(next);
    setRange(defaultRangeForPeriod(next));
  };

  const handleFromChange = (value: string) => {
    const from = fromInputDate(value);
    if (!from) return;
    setRange((prev) => ({
      from,
      to: from > prev.to ? from : prev.to,
    }));
  };

  const handleToChange = (value: string) => {
    const to = fromInputDate(value);
    if (!to) return;
    setRange((prev) => ({
      from: to < prev.from ? to : prev.from,
      to,
    }));
  };

  // No gateway metrics product yet. `/metrics` only exposes incidental BFF proxy
  // counters — and it's served in prod but not proxied in dev, which is why the card
  // wrongly looked "available" only in prod. Force the not-available state until a
  // real gateway metrics API exists; flip this flag (or gate on that API) when it does.
  const GATEWAY_METRICS_ENABLED: boolean = false;
  const gatewayMetrics =
    GATEWAY_METRICS_ENABLED && metricsQuery.isSuccess ? metricsQuery.data : undefined;
  const gatewayAvailable = !!gatewayMetrics;
  const healthOk = healthQuery.data?.status === 'healthy';
  const uptime = gatewayMetrics?.uptime.formatted;
  const topEndpoints = (gatewayMetrics?.requests.topEndpoints ?? [])
    .slice(0, 6)
    .map((row) => ({
      ...row,
      short: row.endpoint.length > 28 ? `…${row.endpoint.slice(-27)}` : row.endpoint,
    }));

  const isRefreshing =
    healthQuery.isFetching || metricsQuery.isFetching || analyticsQuery.isFetching;

  const trendControls = (
    <>
      <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5">
        {PERIODS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => handlePeriodChange(p.value)}
            className={cn(
              'rounded px-2.5 py-1 text-xs font-medium transition-colors',
              period === p.value
                ? 'bg-gray-900 text-white'
                : 'text-gray-600 hover:bg-gray-50',
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5">
        {TREND_MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            onClick={() => setTrendMode(m.value)}
            className={cn(
              'rounded px-2.5 py-1 text-xs font-medium transition-colors',
              trendMode === m.value
                ? 'bg-gray-900 text-white'
                : 'text-gray-600 hover:bg-gray-50',
            )}
          >
            {m.label}
          </button>
        ))}
      </div>
    </>
  );

  return (
    <div className="space-y-4 p-4 md:p-6">
      {!headerVisible &&
        createPortal(
          <div className="fixed bottom-4 right-6 z-50 flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white/95 p-1.5 shadow-lg backdrop-blur-sm">
            {trendControls}
          </div>,
          document.body,
        )}
      <div
        ref={headerRef}
        className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-xl font-bold text-gray-800">Overview</h1>
          <p className="text-xs text-gray-500">
            Users, activity, auth, listings, engagement, and gateway health
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {trendControls}

          <div className="inline-flex rounded-md border border-gray-200 bg-white p-0.5">
            {RANGE_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => setRange(preset.getRange())}
                className={cn(
                  'rounded px-2 py-1 text-xs font-medium transition-colors',
                  activePresetId === preset.id
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50',
                )}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1">
            <input
              type="date"
              value={toInputDate(range.from)}
              max={toInputDate(range.to)}
              onChange={(e) => handleFromChange(e.target.value)}
              className="h-6 w-[118px] border-0 bg-transparent p-0 text-xs text-gray-700 outline-none"
              aria-label="Start date"
            />
            <span className="text-[10px] text-gray-400">→</span>
            <input
              type="date"
              value={toInputDate(range.to)}
              min={toInputDate(range.from)}
              max={toInputDate(new Date())}
              onChange={(e) => handleToChange(e.target.value)}
              className="h-6 w-[118px] border-0 bg-transparent p-0 text-xs text-gray-700 outline-none"
              aria-label="End date"
            />
          </div>

          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => {
              void healthQuery.refetch();
              void metricsQuery.refetch();
              void analyticsQuery.refetch();
            }}
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-5">
        <CompactKpi
          label="Users"
          value={numberFmt.format(kpis.totalUsers)}
          hint={`${numberFmt.format(kpis.activeUsers)} active · ${numberFmt.format(kpis.orgs)} orgs`}
          delta={kpis.usersDelta}
          icon={Users}
          to="/admin/users?test=all"
        />
        <CompactKpi
          label="DAU"
          value={numberFmt.format(kpis.dau)}
          hint={`avg ${numberFmt.format(kpis.dauAvg)} · ${numberFmt.format(kpis.dailyVisitors)} visitors`}
          delta={kpis.dauDelta}
          icon={UserCheck}
        />
        <CompactKpi
          label="MAU"
          value={numberFmt.format(kpis.mau)}
          hint={`${kpis.mau > 0 ? pctFmt.format(kpis.dau / kpis.mau) : '0%'} sticky · ${numberFmt.format(kpis.monthlyVisitors)} visitors`}
          icon={CalendarDays}
        />
        <CompactKpi
          label="Acquired"
          value={numberFmt.format(kpis.acquired)}
          hint={`${numberFmt.format(kpis.signups)} signups`}
          delta={kpis.acquiredDelta}
          icon={UserPlus}
          to={`/admin/analytics/attempts?kind=acquired&from=${toInputDate(range.from)}&to=${toInputDate(range.to)}`}
        />
        <CompactKpi
          label="Logins"
          value={numberFmt.format(kpis.logins)}
          hint={`${numberFmt.format(kpis.sessions)} sessions`}
          delta={kpis.loginDelta}
          icon={LogIn}
          to={`/admin/analytics/attempts?kind=login&success=1&from=${toInputDate(range.from)}&to=${toInputDate(range.to)}`}
        />
        <CompactKpi
          label="Failed logins"
          value={numberFmt.format(kpis.failedLogins)}
          hint={
            kpis.logins > 0
              ? `${((kpis.failedLogins / (kpis.logins + kpis.failedLogins)) * 100).toFixed(1)}% fail rate`
              : undefined
          }
          delta={kpis.failedLoginDelta}
          deltaInvert
          icon={KeyRound}
          to={`/admin/analytics/attempts?kind=login&success=0&from=${toInputDate(range.from)}&to=${toInputDate(range.to)}`}
        />
        <CompactKpi
          label="Churn rate"
          value={pctFmt.format(kpis.churnRate)}
          hint={`${numberFmt.format(kpis.inactiveUsers)} inactive`}
          delta={kpis.churnDelta}
          deltaInvert
          icon={UserMinus}
          to={`/admin/analytics/attempts?kind=churn&from=${toInputDate(range.from)}&to=${toInputDate(range.to)}`}
        />
        <CompactKpi
          label="Listings"
          value={numberFmt.format(kpis.activeListings)}
          hint={`+${numberFmt.format(kpis.newListings)} new · ${numberFmt.format(kpis.totalListings)} total`}
          delta={kpis.listingsDelta}
          icon={ShoppingBag}
          to={`/admin/analytics/attempts?kind=listings&from=${toInputDate(range.from)}&to=${toInputDate(range.to)}`}
        />
        <CompactKpi
          label="Engagements"
          value={numberFmt.format(kpis.engagements)}
          hint={`${engagementTypes.length} types`}
          delta={kpis.engagementDelta}
          icon={Activity}
          to={`/admin/analytics/attempts?kind=engagements&from=${toInputDate(range.from)}&to=${toInputDate(range.to)}`}
        />
        <CompactKpi
          label="Gateway"
          value={gatewayAvailable ? (uptime ?? '—') : '—'}
          hint={
            gatewayAvailable
              ? healthOk
                ? `Healthy · ${gatewayMetrics?.requests.rps ?? 0} rps`
                : 'Degraded'
              : 'Coming soon'
          }
          icon={Server}
        />
        <CompactKpi
          label="Paid orders"
          value={numberFmt.format(commerce.paidOrders)}
          hint="Marketplace orders paid in range"
          icon={ShoppingCart}
        />
        <CompactKpi
          label="GMV"
          value={moneyFmt(commerce.gmv, commerce.gmvCcode)}
          hint="Value of paid orders"
          icon={Banknote}
        />
        <CompactKpi
          label="Revenue collected"
          value={moneyFmt(commerce.collected, commerce.collectedCcode)}
          hint={`${numberFmt.format(commerce.collectedCount)} payments`}
          icon={CreditCard}
        />
        <CompactKpi
          label="Payment success"
          value={commerce.paymentSuccess === null ? '—' : pctFmt.format(commerce.paymentSuccess)}
          hint={`${numberFmt.format(commerce.pendingPayments)} pending · ${numberFmt.format(commerce.canceledPayments)} cancelled`}
          icon={BadgeCheck}
        />
        <CompactKpi
          label="Active subscriptions"
          value={numberFmt.format(commerce.activeSubs)}
          hint={`${numberFmt.format(commerce.pastDueSubs)} past due`}
          icon={Repeat}
        />
        <CompactKpi
          label="Open payouts"
          value={numberFmt.format(commerce.openPayouts)}
          hint={`${moneyFmt(commerce.openPayoutAmount, commerce.collectedCcode)} · all data`}
          icon={Wallet}
        />
        <CompactKpi
          label="Listings sold"
          value={numberFmt.format(commerce.soldListings)}
          hint="Marked sold in range"
          icon={Tag}
        />
        <CompactKpi
          label={`Inquired (${commerce.inquiryDays}d)`}
          value={commerce.inquiredShare === null ? '—' : pctFmt.format(commerce.inquiredShare)}
          hint={`${numberFmt.format(commerce.inquiredListings)} of ${numberFmt.format(commerce.liveListings)} live listings`}
          icon={MessageCircle}
        />
        <CompactKpi
          label="Reply rate"
          value={commerce.replyRate === null ? '—' : pctFmt.format(commerce.replyRate)}
          hint={`${numberFmt.format(commerce.replied)} of ${numberFmt.format(commerce.inbound)} messages`}
          icon={Reply}
        />
        <CompactKpi
          label="Password checks"
          value={numberFmt.format(commerce.passwordChecks)}
          hint={`${numberFmt.format(commerce.passwordChecksFailed)} failed · not sign-ins`}
          icon={ShieldCheck}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <div className="mb-2">
            <h2 className="text-sm font-semibold text-gray-900">Active users</h2>
            <p className="text-[10px] text-gray-400">
              {cumulative ? 'Unique members reached since the range began' : 'Signed-in members, daily vs trailing 30 days'} · {period} ·{' '}
              {formatRangeLabel(range)}
            </p>
          </div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartSeries} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#9ca3af' }} width={36} />
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v: number) => numberFmt.format(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {cumulative ? (
                  <Line
                    type="monotone"
                    dataKey="newActive"
                    name="Unique active users (cum.)"
                    stroke="#4F46E5"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 3 }}
                  />
                ) : (
                  // An array, not a fragment: recharts 2 reads react-is 18, which does
                  // not recognise React 19 fragments, so fragment-wrapped lines vanish.
                  [
                    <Line
                      key="dau"
                      type="monotone"
                      dataKey="dau"
                      name={`${perDay}DAU`}
                      stroke="#4F46E5"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 3 }}
                    />,
                    <Line
                      key="mau"
                      type="monotone"
                      dataKey="mau"
                      name="MAU"
                      stroke="#94A3B8"
                      strokeWidth={2}
                      strokeDasharray="4 3"
                      dot={false}
                    />,
                  ]
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <div className="mb-2">
            <h2 className="text-sm font-semibold text-gray-900">Visitors</h2>
            <p className="text-[10px] text-gray-400">
              {cumulative ? 'Unique visitors reached since the range began' : 'Members and anonymous people, daily vs trailing 30 days'} · {period} ·{' '}
              {formatRangeLabel(range)}
            </p>
          </div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartSeries} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#9ca3af' }} width={36} />
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v: number) => numberFmt.format(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {cumulative ? (
                  <Line
                    type="monotone"
                    dataKey="newVisitors"
                    name="Unique visitors (cum.)"
                    stroke="#0891B2"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 3 }}
                  />
                ) : (
                  // An array, not a fragment: recharts 2 reads react-is 18, which does
                  // not recognise React 19 fragments, so fragment-wrapped lines vanish.
                  [
                    <Line
                      key="visitors"
                      type="monotone"
                      dataKey="visitors"
                      name={period === 'daily' ? 'Daily visitors' : 'Avg daily visitors'}
                      stroke="#0891B2"
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 3 }}
                    />,
                    <Line
                      key="visitors30d"
                      type="monotone"
                      dataKey="visitors30d"
                      name="Visitors (30d)"
                      stroke="#94A3B8"
                      strokeWidth={2}
                      strokeDasharray="4 3"
                      dot={false}
                    />,
                  ]
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <div className="mb-2">
            <h2 className="text-sm font-semibold text-gray-900">User acquisition & churn</h2>
            <p className="text-[10px] text-gray-400">
              {period} · {modeLabel} · {formatRangeLabel(range)}
            </p>
          </div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartSeries} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#9ca3af' }} width={32} />
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v: number) => numberFmt.format(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area
                  type="monotone"
                  dataKey="acquired"
                  name={trendMode === 'cumulative' ? 'Acquired (cum.)' : 'Acquired'}
                  stroke="#4F46E5"
                  fill="#6366F1"
                  fillOpacity={0.15}
                  strokeWidth={1.5}
                />
                <Area
                  type="monotone"
                  dataKey="churned"
                  name={trendMode === 'cumulative' ? 'Churned (cum.)' : 'Churned'}
                  stroke="#E11D48"
                  fill="#FB7185"
                  fillOpacity={0.12}
                  strokeWidth={1.5}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <div className="mb-2">
            <h2 className="text-sm font-semibold text-gray-900">Signups & logins</h2>
            <p className="text-[10px] text-gray-400">
              {period} · {modeLabel} · {formatRangeLabel(range)}
            </p>
          </div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartSeries} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#9ca3af' }} width={40} />
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v: number) => numberFmt.format(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area
                  type="monotone"
                  dataKey="signups"
                  name={trendMode === 'cumulative' ? 'Signups (cum.)' : 'Signups'}
                  stroke="#6366F1"
                  fill="#A5B4FC"
                  fillOpacity={0.2}
                  strokeWidth={1.5}
                />
                <Area
                  type="monotone"
                  dataKey="logins"
                  name={trendMode === 'cumulative' ? 'Logins (cum.)' : 'Logins'}
                  stroke="#0D9488"
                  fill="#2DD4BF"
                  fillOpacity={0.12}
                  strokeWidth={1.5}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <div className="mb-2">
            <h2 className="text-sm font-semibold text-gray-900">Listing trends</h2>
            <p className="text-[10px] text-gray-400">
              New listings vs listings live at the end of each period (estimated from posting
              and expiry dates) · {modeLabel} · {formatRangeLabel(range)}
            </p>
          </div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartSeries} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                <YAxis
                  yAxisId="left"
                  allowDecimals={false}
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  width={36}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  allowDecimals={false}
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  width={40}
                />
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v: number) => numberFmt.format(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="listingsNew"
                  name={trendMode === 'cumulative' ? 'New listings (cum.)' : 'New listings'}
                  stroke="#059669"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="listingsActive"
                  name="Active listings (est.)"
                  stroke="#64748B"
                  strokeWidth={2}
                  strokeDasharray="4 3"
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <div className="mb-2">
            <h2 className="text-sm font-semibold text-gray-900">Engagement trend</h2>
            <p className="text-[10px] text-gray-400">
              {period} · {modeLabel} · {formatRangeLabel(range)}
            </p>
          </div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartSeries} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#9ca3af' }} width={40} />
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v: number) => numberFmt.format(v)} />
                <Line
                  type="monotone"
                  dataKey="engagements"
                  name={trendMode === 'cumulative' ? 'Engagements (cum.)' : 'Engagements'}
                  stroke="#D97706"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <TrendChart
          title="Login outcomes"
          subtitle={chartSubtitle}
          data={chartSeries}
          rightFmt={pctLabel}
          action={
            <label
              className="flex shrink-0 cursor-pointer items-center gap-1 text-[11px] text-gray-500"
              title="Sign-ins to this admin app by staff"
            >
              <input
                type="checkbox"
                checked={staffLogins}
                onChange={(e) => setStaffLogins(e.target.checked)}
              />
              Staff sign-ins
            </label>
          }
          series={[
            {
              key: (r) => r.logins - (staffLogins ? 0 : r.adminLogins),
              name: 'Successful',
              color: '#0D9488',
              kind: 'bar',
              stack: 'a',
            },
            {
              key: (r) => r.failedLogins - (staffLogins ? 0 : r.adminFailedLogins),
              name: 'Failed',
              color: '#E11D48',
              kind: 'bar',
              stack: 'a',
            },
            {
              key: (r) =>
                staffLogins
                  ? pctOf(r.failedLogins, r.logins + r.failedLogins)
                  : pctOf(
                      r.failedLogins - r.adminFailedLogins,
                      r.logins + r.failedLogins - r.adminLogins - r.adminFailedLogins,
                    ),
              name: 'Failure rate',
              color: '#64748B',
              kind: 'line',
              right: true,
              fmt: pctLabel,
            },
          ]}
        />

        <TrendChart
          title="New vs returning visitors"
          subtitle={`First visit ever vs seen before${period === 'daily' ? '' : ', days added up'} · ${chartSubtitle}`}
          data={chartSeries}
          series={[
            { key: 'visitorsNew', name: 'New', color: '#0891B2', kind: 'bar', stack: 'a' },
            { key: 'visitorsReturning', name: 'Returning', color: '#94A3B8', kind: 'bar', stack: 'a' },
          ]}
        />

        <TrendChart
          title="New vs returning members"
          subtitle={`First active day ever vs seen before${period === 'daily' ? '' : ', days added up'} · ${chartSubtitle}`}
          data={chartSeries}
          series={[
            { key: 'membersNew', name: 'New', color: '#4F46E5', kind: 'bar', stack: 'a' },
            { key: 'membersReturning', name: 'Returning', color: '#94A3B8', kind: 'bar', stack: 'a' },
          ]}
        />

        <TrendChart
          title="Orders & GMV"
          subtitle={`Marketplace orders paid · ${chartSubtitle}`}
          data={chartSeries}
          rightFmt={(v) => numberFmt.format(v)}
          series={[
            { key: 'ordersPaid', name: 'Paid orders', color: '#4F46E5', kind: 'bar' },
            {
              key: 'gmv',
              name: `GMV (${commerce.gmvCcode})`,
              color: '#D97706',
              kind: 'line',
              right: true,
              fmt: (v) => moneyFmt(v, commerce.gmvCcode),
            },
          ]}
        />

        <TrendChart
          title="Payments"
          subtitle={`Platform payments by outcome, and money collected · ${chartSubtitle}`}
          data={chartSeries}
          rightFmt={(v) => numberFmt.format(v)}
          series={[
            { key: 'payCollected', name: 'Collected', color: '#059669', kind: 'bar', stack: 'a' },
            { key: 'payPending', name: 'Pending', color: '#F59E0B', kind: 'bar', stack: 'a' },
            { key: 'payCanceled', name: 'Cancelled', color: '#E11D48', kind: 'bar', stack: 'a' },
            {
              key: 'collected',
              name: `Revenue (${commerce.collectedCcode})`,
              color: '#4F46E5',
              kind: 'line',
              right: true,
              fmt: (v) => moneyFmt(v, commerce.collectedCcode),
            },
          ]}
        />

        <TrendChart
          title="Seller responsiveness"
          subtitle={`Replies by how fast they came, and share of messages answered · ${chartSubtitle}`}
          data={chartSeries}
          rightFmt={pctLabel}
          series={[
            { key: 'replyLt5m', name: 'Under 5 min', color: '#059669', kind: 'bar', stack: 'a' },
            { key: 'replyLt30m', name: 'Under 30 min', color: '#34D399', kind: 'bar', stack: 'a' },
            { key: 'replyLt2h', name: 'Under 2 h', color: '#A3E635', kind: 'bar', stack: 'a' },
            { key: 'replyLt12h', name: 'Under 12 h', color: '#FBBF24', kind: 'bar', stack: 'a' },
            { key: 'replyLt48h', name: 'Under 2 days', color: '#FB923C', kind: 'bar', stack: 'a' },
            { key: 'replyGte48h', name: 'Over 2 days', color: '#E11D48', kind: 'bar', stack: 'a' },
            {
              key: (r) => pctOf(r.replied, r.inbound),
              name: 'Reply rate',
              color: '#4F46E5',
              kind: 'line',
              right: true,
              fmt: pctLabel,
            },
          ]}
        />

        <TrendChart
          title="Subscriptions"
          subtitle={`Started and ended, and how many were in force (active, trialing, paused or past due) · ${chartSubtitle}`}
          data={chartSeries}
          series={[
            { key: 'subsStarted', name: cumulative ? 'Started (cum.)' : 'Started', color: '#059669', kind: 'bar' },
            { key: 'subsEnded', name: cumulative ? 'Ended (cum.)' : 'Ended', color: '#E11D48', kind: 'bar' },
            { key: 'subsActive', name: 'In force', color: '#4F46E5', kind: 'line', right: true },
            // Drawn only from the first day a daily snapshot recorded it.
            { key: 'subsPastDue', name: 'Past due', color: '#D97706', kind: 'line', right: true },
          ]}
        />

        <TrendChart
          title="New listings by type"
          subtitle={`Biggest types in the range · ${chartSubtitle}`}
          data={typeSeries.rows}
          series={typeSeries.names.map((name, i) => ({
            key: name,
            name,
            color: TYPE_COLORS[i % TYPE_COLORS.length],
            kind: 'bar' as const,
            stack: 'a',
          }))}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {(
          [
            ['device_types', 'Device type', `Visitors · ${formatRangeLabel(range)}`, '#4F46E5'],
            ['device_os', 'Operating system', `Visitors · ${formatRangeLabel(range)}`, '#0891B2'],
            ['device_models', 'Device models', 'Members with the device on their profile · today', '#7C3AED'],
            ['gender', 'Gender', 'All members · today', '#E11D48'],
            ['age_bands', 'Age', 'All members, from date of birth · today', '#D97706'],
            ['regions', 'Region', `Visitors · ${formatRangeLabel(range)}`, '#059669'],
          ] as const
        ).map(([key, title, subtitle, color]) => (
          <MixCard
            key={key}
            title={title}
            subtitle={subtitle}
            rows={overview?.audience?.[key]}
            color={color}
            loading={analyticsQuery.isFetching}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <h2 className="mb-2 text-sm font-semibold text-gray-900">Marketplace funnel</h2>
          <p className="mb-2 text-[10px] text-gray-400">
            Totals for the range, not one group followed through · % is of the step before ·
            visitors are not split live/test
          </p>
          <ul className="space-y-1.5">
            {funnel.map((row, i) => {
              // A share of the step before only means something in the same unit.
              const prev = i > 0 && funnel[i - 1].unit === row.unit ? funnel[i - 1].count : 0;
              return (
                <li key={row.label} className="flex items-center gap-2">
                  <span className="w-24 shrink-0 truncate text-[11px] text-gray-600">{row.label}</span>
                  <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-100">
                    <div
                      className="h-full rounded-full bg-indigo-500"
                      style={{ width: `${row.count > 0 ? Math.max(3, (row.count / maxFunnel) * 100) : 0}%` }}
                    />
                  </div>
                  <span className="w-12 shrink-0 text-right text-[11px] tabular-nums text-gray-800">
                    {numberFmt.format(row.count)}
                  </span>
                  <span className="w-10 shrink-0 text-right text-[10px] tabular-nums text-gray-400">
                    {prev > 0 ? pctFmt.format(row.count / prev) : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <h2 className="mb-2 text-sm font-semibold text-gray-900">Listing mix</h2>
          <p className="mb-2 text-[10px] text-gray-400">Active listings by category</p>
          <ul className="max-h-48 space-y-1.5 overflow-y-auto">
            {listingMix.map((row) => (
              <li key={row.key} className="flex items-center gap-2">
                <span className="w-24 shrink-0 truncate text-[11px] text-gray-600">{row.label}</span>
                <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full bg-emerald-500"
                    style={{ width: `${Math.max(3, (row.count / maxListingCat) * 100)}%` }}
                  />
                </div>
                <span className="w-12 shrink-0 text-right text-[11px] tabular-nums text-gray-800">
                  {numberFmt.format(row.count)}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <h2 className="mb-2 text-sm font-semibold text-gray-900">Engagement mix</h2>
          <p className="mb-2 text-[10px] text-gray-400">By engagement type</p>
          <ul className="max-h-48 space-y-1.5 overflow-y-auto">
            {engagementTypes.map((row) => (
              <li key={row.key} className="flex items-center gap-2">
                <span className="w-20 shrink-0 truncate text-[11px] text-gray-600 sm:w-24">
                  {row.label}
                </span>
                <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full bg-amber-400"
                    style={{ width: `${Math.max(3, (row.count / maxEngagement) * 100)}%` }}
                  />
                </div>
                <span className="w-12 shrink-0 text-right text-[11px] tabular-nums text-gray-800">
                  {numberFmt.format(row.count)}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Gateway performance</h2>
              <p className="text-[10px] text-gray-400">Live BFF metrics when available</p>
            </div>
            {gatewayAvailable && (
              <div className="flex flex-wrap gap-3 text-[11px] tabular-nums text-gray-600">
                <span>
                  RPS <strong className="text-gray-900">{gatewayMetrics?.requests.rps ?? 0}</strong>
                </span>
                <span>
                  Err <strong className="text-gray-900">{gatewayMetrics?.errors.rate ?? 0}%</strong>
                </span>
                <span>
                  p95{' '}
                  <strong className="text-gray-900">
                    {Math.round(gatewayMetrics?.timing.p95 ?? 0)}ms
                  </strong>
                </span>
              </div>
            )}
          </div>
          {gatewayAvailable ? (
            <div className="h-36">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={topEndpoints}
                  layout="vertical"
                  margin={{ left: 4, right: 8, top: 0, bottom: 0 }}
                >
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="short"
                    width={110}
                    tick={{ fontSize: 10, fill: '#6b7280' }}
                  />
                  <Tooltip
                    formatter={(value: number) => [numberFmt.format(value), 'Requests']}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.endpoint ?? ''}
                  />
                  <Bar dataKey="count" fill="#4F46E5" radius={[0, 3, 3, 0]} barSize={10} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="flex h-36 flex-col items-center justify-center gap-1.5 rounded-md border border-dashed border-gray-200 bg-gray-50/50 text-center">
              <Server className="h-5 w-5 text-gray-300" />
              <p className="text-xs font-medium text-gray-500">Gateway metrics not available yet</p>
              <p className="text-[10px] text-gray-400">Coming soon</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// The overview is company-wide reporting. Everyone lands on /admin, so staff
// without the reports permission get a plain landing instead of the figures.
const DashboardGate = () => {
  const { hasPermission, isSuperAdmin, isLoading } = useHasPermission(PERMISSIONS.report.view);
  if (isLoading) return null;
  if (isSuperAdmin || hasPermission) return <Dashboard />;
  return (
    <div className="flex min-h-[60vh] items-center justify-center px-6">
      <div className="max-w-md text-center">
        <h1 className="mb-2 text-2xl font-bold text-gray-900">Welcome</h1>
        <p className="text-sm text-gray-600">Pick a section from the menu to get started.</p>
      </div>
    </div>
  );
};

export default DashboardGate;
